import { array, boolean, object, string } from "yup";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import {
  normalizeSocialUrlInput,
  normalizeUrl,
  removeTrailingPunctuation,
  stripUrlProtocol,
} from "@shared-kernel/primary/shared/url-input/url-input.helpers";
import { websiteUrlInvalidMessage, websiteUrlValidation } from "@shared-kernel/primary/forms/validations/website-url";
import { specializationsArraySchema } from "@shared-kernel/primary/shared/specializations-picker/specializations-picker.schema";
import type { LeafId } from "@academy-context/shared/domain/types/enums/specializations";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { TeacherVM, UpdateTeacherPublicProfileBody } from "@shared-kernel/application/ports/teacher/teacher-repository";

export const URL_INVALID = websiteUrlInvalidMessage();
export const INSTAGRAM_URL_INVALID = "Ce n'est pas un lien Instagram. Exemple : instagram.com/votre-profil";
export const YOUTUBE_URL_INVALID = "Ce n'est pas un lien YouTube. Exemple : youtube.com/@votre-chaine";
export const TIKTOK_URL_INVALID = "Ce n'est pas un lien TikTok. Exemple : tiktok.com/@votre-profil";
export const FACEBOOK_URL_INVALID = "Ce n'est pas un lien Facebook. Exemple : facebook.com/votre-page";
export const DESCRIPTION_MAX_LENGTH = 60;
export const DESCRIPTION_MAX_ERROR = `La description ne doit pas dépasser ${DESCRIPTION_MAX_LENGTH} caractères`;
export const PEDAGOGICAL_EXPERIENCE_MIN_LENGTH = 1000;
export const PEDAGOGICAL_EXPERIENCE_MIN_ERROR = `L'expérience pédagogique doit contenir au moins ${PEDAGOGICAL_EXPERIENCE_MIN_LENGTH} caractères`;
export const PUBLIC_TEACHING_VENUES_REQUIRED = "Sélectionnez au moins un studio ou activez la visio";

export const INSTAGRAM_URL_REGEX = /^https:\/\/(www\.)?instagram\.com\/.+/i;
export const YOUTUBE_URL_REGEX = /^https:\/\/((www\.|m\.)?youtube\.com|youtu\.be)\/.+/i;
export const TIKTOK_URL_REGEX = /^https:\/\/(www\.|vm\.|vt\.|m\.)?tiktok\.com\/.+/i;
export const FACEBOOK_URL_REGEX = /^https:\/\/((www\.|m\.|web\.)?facebook\.com|fb\.com|fb\.me)\/.+/i;

// Re-exported for local consumers/tests; canonical definitions live in the shared kernel.
export { normalizeSocialUrlInput, normalizeUrl, stripUrlProtocol };

export interface TeacherPublicProfileFormInputs {
  description: string;
  pedagogicalExperience: string;
  specializations: LeafId[];
  publicStudioIds: string[];
  offersOnlineCoaching: boolean;
  websiteUrl: string;
  instagramUrl: string;
  youtubeUrl: string;
  tiktokUrl: string;
  facebookUrl: string;
  isPublicProfileVisibleByTeacher: boolean;
  isEmailVisibleOnPublicProfile: boolean;
}

export const teacherPublicProfileDefaultValues: TeacherPublicProfileFormInputs = {
  description: "",
  pedagogicalExperience: "",
  specializations: [],
  publicStudioIds: [],
  offersOnlineCoaching: false,
  websiteUrl: "",
  instagramUrl: "",
  youtubeUrl: "",
  tiktokUrl: "",
  facebookUrl: "",
  isPublicProfileVisibleByTeacher: true,
  isEmailVisibleOnPublicProfile: true,
};

export const publicProfileSharedShape = {
  description: string().trim().max(DESCRIPTION_MAX_LENGTH, DESCRIPTION_MAX_ERROR).required(REQUIRED_FIELD),
  pedagogicalExperience: string().trim().required(REQUIRED_FIELD).min(PEDAGOGICAL_EXPERIENCE_MIN_LENGTH, PEDAGOGICAL_EXPERIENCE_MIN_ERROR),
  specializations: specializationsArraySchema,
  offersOnlineCoaching: boolean().required(REQUIRED_FIELD),
  websiteUrl: websiteUrlValidation().defined(),
  instagramUrl: string()
    .transform(removeTrailingPunctuation)
    .url(INSTAGRAM_URL_INVALID)
    .matches(INSTAGRAM_URL_REGEX, { message: INSTAGRAM_URL_INVALID, excludeEmptyString: true })
    .defined(),
  youtubeUrl: string()
    .transform(removeTrailingPunctuation)
    .url(YOUTUBE_URL_INVALID)
    .matches(YOUTUBE_URL_REGEX, { message: YOUTUBE_URL_INVALID, excludeEmptyString: true })
    .defined(),
  tiktokUrl: string()
    .transform(removeTrailingPunctuation)
    .url(TIKTOK_URL_INVALID)
    .matches(TIKTOK_URL_REGEX, { message: TIKTOK_URL_INVALID, excludeEmptyString: true })
    .defined(),
  facebookUrl: string()
    .transform(removeTrailingPunctuation)
    .url(FACEBOOK_URL_INVALID)
    .matches(FACEBOOK_URL_REGEX, { message: FACEBOOK_URL_INVALID, excludeEmptyString: true })
    .defined(),
  isPublicProfileVisibleByTeacher: boolean().required(REQUIRED_FIELD),
  isEmailVisibleOnPublicProfile: boolean().required(REQUIRED_FIELD),
};

export const teacherPublicProfileSchemaShape = {
  ...publicProfileSharedShape,
  publicStudioIds: array(string().required()).required(REQUIRED_FIELD),
};

export const teacherPublicProfileSchema = object()
  .shape(teacherPublicProfileSchemaShape)
  .test("public-teaching-venues-required", PUBLIC_TEACHING_VENUES_REQUIRED, function (value) {
    if (!value) return true;
    if ((value.publicStudioIds && value.publicStudioIds.length > 0) || value.offersOnlineCoaching) return true;
    return this.createError({ path: "publicStudioIds", message: PUBLIC_TEACHING_VENUES_REQUIRED });
  });

export const formatTeacherDataToPublicProfileFormData = (teacher: TeacherVM): TeacherPublicProfileFormInputs => ({
  description: teacher.description ?? "",
  pedagogicalExperience: teacher.pedagogicalExperience ?? "",
  specializations: teacher.specializations,
  publicStudioIds: teacher.publicTeachingVenues.studioIds,
  offersOnlineCoaching: teacher.publicTeachingVenues.offersOnlineCoaching,
  websiteUrl: teacher.websiteUrl ?? "",
  instagramUrl: teacher.instagramUrl ?? "",
  youtubeUrl: teacher.youtubeUrl ?? "",
  tiktokUrl: teacher.tiktokUrl ?? "",
  facebookUrl: teacher.facebookUrl ?? "",
  isPublicProfileVisibleByTeacher: teacher.isPublicProfileVisibleByTeacher,
  isEmailVisibleOnPublicProfile: teacher.isEmailVisibleOnPublicProfile,
});

export const normalizeWebsiteUrlForSubmit = (value: string): Nullable<string> => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return trimmed.replace(/^([a-z][a-z0-9+.-]*:\/\/[^/?#]+)/i, match => match.toLowerCase());
};

export const normalizeSocialUrlForSubmit = (value: string): Nullable<string> => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return trimmed.replace(/^http:\/\//i, "https://").toLowerCase();
};

export const formatPublicProfileFormDataToBody = (data: TeacherPublicProfileFormInputs): UpdateTeacherPublicProfileBody => ({
  description: data.description,
  pedagogicalExperience: data.pedagogicalExperience,
  specializations: data.specializations,
  publicTeachingVenues: {
    studioIds: data.publicStudioIds,
    offersOnlineCoaching: data.offersOnlineCoaching,
  },
  websiteUrl: normalizeWebsiteUrlForSubmit(data.websiteUrl),
  instagramUrl: normalizeSocialUrlForSubmit(data.instagramUrl),
  youtubeUrl: normalizeSocialUrlForSubmit(data.youtubeUrl),
  tiktokUrl: normalizeSocialUrlForSubmit(data.tiktokUrl),
  facebookUrl: normalizeSocialUrlForSubmit(data.facebookUrl),
  isPublicProfileVisibleByTeacher: data.isPublicProfileVisibleByTeacher,
  isEmailVisibleOnPublicProfile: data.isEmailVisibleOnPublicProfile,
});
